<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12">
    <UiCard class="w-full max-w-md">
      <UiCardHeader class="space-y-1 text-center">
        <UiCardTitle>{{ $t('auth.resetPassword.title') }}</UiCardTitle>
        <UiCardDescription>{{ $t('auth.resetPassword.description') }}</UiCardDescription>
      </UiCardHeader>
      <UiCardContent class="space-y-4">
        <!-- Invalid Token Alert -->
        <UiAlert v-if="!token" variant="destructive">
          <Icon name="tabler:alert-circle" size="16" />
          <UiAlertDescription>{{ $t('auth.resetPassword.invalidToken') }}</UiAlertDescription>
        </UiAlert>

        <!-- Success Alert -->
        <UiAlert v-if="success" variant="success">
          <Icon name="tabler:circle-check" size="16" />
          <UiAlertDescription>{{ $t('auth.resetPassword.successMessage') }}</UiAlertDescription>
        </UiAlert>

        <!-- Error Alert -->
        <UiAlert v-if="serverError" variant="destructive">
          <Icon name="tabler:alert-circle" size="16" />
          <UiAlertDescription>{{ serverError }}</UiAlertDescription>
        </UiAlert>

        <!-- Reset Password Form -->
        <VeeForm
          v-if="token && !success"
          :validation-schema="resetPasswordSchema"
          @submit="handleResetPassword"
          class="space-y-4"
          v-slot="{ meta }"
        >
          <div class="space-y-2">
            <UiLabel for="password">{{ $t('auth.fields.newPassword') }}</UiLabel>
            <VeeField name="password" v-slot="{ field, errorMessage }">
              <UiInput
                id="password"
                v-bind="field"
                type="password"
                :placeholder="$ts('auth.fields.newPasswordPlaceholder')"
                autocomplete="new-password"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>
          <div class="space-y-2">
            <UiLabel for="confirmPassword">{{ $t('auth.fields.confirmPassword') }}</UiLabel>
            <VeeField name="confirmPassword" v-slot="{ field, errorMessage }">
              <UiInput
                id="confirmPassword"
                v-bind="field"
                type="password"
                :placeholder="$ts('auth.fields.confirmPasswordPlaceholder')"
                autocomplete="new-password"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>
          <UiButton type="submit" class="w-full" :disabled="isLoading || !meta.valid">
            <Icon v-if="isLoading" name="tabler:loader-2" size="16" class="animate-spin" />
            {{ $t('auth.resetPassword.submit') }}
          </UiButton>
        </VeeForm>

        <!-- Back to Login -->
        <div v-if="success || !token" class="text-center">
          <NuxtLink :to="$localeRoute('/account/login')">
            <UiButton variant="outline" class="w-full">
              <Icon name="tabler:arrow-left" size="16" />
              {{ $t('auth.resetPassword.backToLogin') }}
            </UiButton>
          </NuxtLink>
        </div>
      </UiCardContent>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n, useRoute, definePageMeta, useSeoMeta } from '#imports';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useAuthClient } from '#composables/useAuthClient';

definePageMeta({
  middleware: ['guest'],
});

const { $t, $ts, $localeRoute } = useI18n();
useSeoMeta({ title: () => $ts('auth.resetPassword.seo.title') });

const resetPasswordSchema = toTypedSchema(
  z.object({
    password: z.string()
      .min(1, $ts('auth.validation.passwordRequired'))
      .min(8, $ts('auth.validation.passwordMinLength')),
    confirmPassword: z.string()
      .min(1, $ts('auth.validation.confirmPasswordRequired')),
  }).refine((data) => data.password === data.confirmPassword, {
    message: $ts('auth.validation.passwordsMismatch'),
    path: ['confirmPassword'],
  })
);

const authClient = useAuthClient();
const route = useRoute();

const token = computed(() => route.query.token as string | undefined);
const isLoading = ref(false);
const serverError = ref<string | null>(null);
const success = ref(false);

async function handleResetPassword(values: Record<string, unknown>) {
  const password = values.password as string;

  if (!token.value) return;

  isLoading.value = true;
  serverError.value = null;

  try {
    const result = await authClient.resetPassword({
      token: token.value,
      newPassword: password,
    });

    if (result.error) {
      serverError.value = result.error.message || $ts('auth.resetPassword.invalidToken');
      return;
    }

    success.value = true;
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : $ts('auth.resetPassword.invalidToken');
  } finally {
    isLoading.value = false;
  }
}
</script>
